import { fullMonthAndYear } from "@similarweb/pro-logic-components";
import { translate } from "app/filters/i18nFilter";
import AdNetworkSignalEvent from "app/pages/sales-intelligence/sub-modules/signals/models/ad-network/AdNetworkSignalEvent";
import type SignalAdNetwork from "app/pages/sales-intelligence/sub-modules/signals/models/ad-network/SignalAdNetwork";
import SignalType from "app/pages/sales-intelligence/sub-modules/signals/models/core/SignalType";
import type { SignalsDescription } from "app/pages/sales-intelligence/sub-modules/signals/models/miscellaneous/SignalsDescription";
import TechnologyChangesType from "app/pages/sales-intelligence/sub-modules/signals/models/technology/TechnologyChangesType";
import dateTimeService from "app/services/date-time/dateTimeService";

import type { CompanyInfoBase } from "../../../../shared/types/company";
import type {
    AdNetworkSignalEventDto,
    IntentSignalEventDto,
    NewsSignalEventDto,
    SignalEventDto,
    TechnologySignalEventDto,
} from "../../../types/signals";
import { signalTitleTextMap } from "../maps/newsSignalTextMap";
import { getTechnologySignalEventActionText } from "./getTechnologySignalEventActionText";
import { getTrafficSignalEventTitle } from "./getTrafficSignalEventTitle";
import { getIntentSignalEventsFromDto } from "./intent";

const getNewsSignalEventTitle = (signal: NewsSignalEventDto, companyName: string): string => {
    const { categories } = signal;
    const subtype = categories[0];

    const signalTitleTextMapValue = signalTitleTextMap[subtype];

    return typeof signalTitleTextMapValue === "function"
        ? signalTitleTextMapValue(signal, companyName)
        : signalTitleTextMapValue;
};

const getTechnologySignalTechnologiesText = (signal: TechnologySignalEventDto): string => {
    const { technologies, changeType } = signal;

    if (changeType === TechnologyChangesType.RenewalComingUp && technologies.length > 1) {
        if (technologies.length === 2) {
            return technologies.join(" and ");
        }

        const list = [
            ...technologies.slice(0, technologies.length - 1),
            `and ${technologies[technologies.length - 1]}`,
        ];

        return list.join(", ");
    }

    return technologies.length === 1 ? technologies[0] : `${technologies.length} technologies`;
};

const getTechnologySignalRenewalDate = (signal: TechnologySignalEventDto): string => {
    const { changeType, renewalDate } = signal;

    if (changeType !== TechnologyChangesType.RenewalComingUp || !renewalDate) {
        return "";
    }

    return dateTimeService.formatWithMoment(renewalDate, fullMonthAndYear);
};

const getTechnologySignalEventTitle = (signal: TechnologySignalEventDto, companyName: string): string => {
    const technologyText = getTechnologySignalTechnologiesText(signal);
    const renewalDateText = getTechnologySignalRenewalDate(signal);

    return [
        companyName,
        getTechnologySignalEventActionText(signal),
        technologyText,
        renewalDateText ? `in ${renewalDateText}` : null,
    ]
        .filter((v) => v)
        .join(" ");
};

const getIntentSignalEventTitle = (signal: IntentSignalEventDto, companyName: string): string => {
    const signalEvents = getIntentSignalEventsFromDto(signal);
    const signalEventsCount = signalEvents.length;

    return translate(
        signalEventsCount > 1
            ? "si.signals.intent.company-overview.title"
            : "si.signals.intent.company-overview.title.single",
        {
            companyName,
            topicsAmount: signalEventsCount,
        },
    );
};

const getAdNetworkSignalEventActionText = (signal: AdNetworkSignalEventDto, companyName: string): string => {
    const { changeType } = signal;

    const actionText =
        changeType === AdNetworkSignalEvent.StartedWorkingWith ? "started to work with" : "stopped working with";
    return `${companyName} ${actionText}`;
};

const getAdNetworkSignalEventTitle = (
    signal: AdNetworkSignalEventDto,
    companyName: string,
    signalsDescription: Readonly<SignalsDescription> | null,
): string => {
    const { adNetworkIds } = signal;
    if (adNetworkIds.length > 1) {
        return `${getAdNetworkSignalEventActionText(signal, companyName)} ${adNetworkIds.length} ad-networks`;
    }

    if (!signalsDescription) {return "";}
    const { adNetworks } = signalsDescription;
    const networkText = adNetworks.find((network: SignalAdNetwork) => network.id === adNetworkIds[0])?.name;
    return `${getAdNetworkSignalEventActionText(signal, companyName)} ${networkText}`;
};

export const getSignalEventTitle = (
    signal: SignalEventDto,
    companyInfo: CompanyInfoBase | undefined,
    signalsDescription: Readonly<SignalsDescription> | null,
): string => {
    const { type } = signal;
    const companyName = companyInfo?.companyName || "";
    const domain = companyInfo?.domain?.name || "";

    if (type === SignalType.News) {
        return getNewsSignalEventTitle(signal, companyName);
    } else if (type === SignalType.Technology) {
        return getTechnologySignalEventTitle(signal, companyName);
    } else if (type === SignalType.AdNetwork) {
        return getAdNetworkSignalEventTitle(signal, companyName, signalsDescription);
    } else if (type === SignalType.Traffic) {
        return getTrafficSignalEventTitle(signal, domain);
    } else if (type === SignalType.Intent) {
        return getIntentSignalEventTitle(signal, companyName);
    }
    return "";
};
